export function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return ''
  const { hostname, protocol } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1')
    return 'http://api.local.test:3001'
  const domain = hostname.split('.').slice(-2).join('.')
  // api.homenow.report has no DNS record nor certificate (Oct 24 2026: every
  // email sent from homenow.report failed with ERR_NAME_NOT_RESOLVED and the
  // rep only saw "Delivery could not be confirmed"). Same server, so the
  // HOMENOW site talks to api.ecohomereports.com (CORS allows any origin).
  return (
    protocol +
    '//api.' +
    (domain === 'homenow.report' ? 'ecohomereports.com' : domain)
  )
}
export interface SignedAttachment {
  pdfBase64: string
  fileName: string
}
// Upload budget for the signed PDF from a field connection plus server SMTP:
// 60s floor, +8s per MB of body, capped at 3 min. A 10MB body gets 110s.
export function attachmentTimeoutMs(bodyLength: number): number {
  return Math.min(
    180000,
    Math.max(60000, 30000 + Math.ceil((bodyLength / 1048576) * 8000)),
  )
}

export async function describeHttpFailure(res: {
  status: number
  json(): Promise<unknown>
}): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { err?: unknown }
  const detail = typeof body?.err === 'string' && body.err ? body.err : ''
  if (res.status === 413)
    return 'The server rejected the PDF as too large (HTTP 413).'
  return (detail || 'Server error') + ' (HTTP ' + res.status + ')'
}

export function describeFetchFailure(e: unknown, timeoutMs: number): string {
  const err = e as { name?: string; message?: string } | null
  if (err?.name === 'AbortError')
    return (
      'The email request timed out after ' + Math.round(timeoutMs / 1000) + 's'
    )
  // fetch rejects with a TypeError when no readable response arrives: offline,
  // or a server error answered without CORS headers (e.g. an oversized body).
  if (err?.name === 'TypeError')
    return (
      'No readable response from the email server (' +
      (err.message || 'network error') +
      ')'
    )
  return err?.message || String(e)
}

export const SECTIONS_NOT_SUPPORTED =
  'The email server is not ready for section links yet. Contact the dev team before sending again.'

export interface SendOptions {
  /** Firebase ID token of whoever is sending (rep or signing session). */
  idToken?: string | undefined
  /** Signing link only: what the customer will see. */
  sections?: readonly string[] | undefined
}

export default async function sendContractByEmail(
  customerName: string,
  customerEmail: string,
  customerID: string,
  signedAttachment?: SignedAttachment,
  { idToken, sections }: SendOptions = {},
): Promise<void> {
  // Default manual behavior unchanged; Finish reuses the existing attachment endpoint.
  const endpoint = signedAttachment ? '/send-contract' : '/signing-tokens'
  const body = JSON.stringify(
    signedAttachment
      ? { customerEmail, customerName, ...signedAttachment }
      : {
          customerID,
          customerEmail,
          customerName,
          ...(sections ? { sections } : {}),
        },
  )
  const controller = signedAttachment ? new AbortController() : null
  const timeoutMs = attachmentTimeoutMs(body.length)
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null
  try {
    let res: Response
    try {
      res = await fetch(getApiBaseUrl() + endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: 'Bearer ' + idToken } : {}),
        },
        body,
        ...(controller ? { signal: controller.signal } : {}),
      })
    } catch (e) {
      if (!signedAttachment) throw e
      throw new Error(describeFetchFailure(e, timeoutMs))
    }
    if (!res.ok) {
      if (!signedAttachment) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as any).err || 'Server error ' + res.status)
      }
      throw new Error(await describeHttpFailure(res))
    }
    if (signedAttachment && (await res.json().catch(() => null))?.ok !== true)
      throw new Error('The server did not confirm email delivery.')
    // A server without sections mails a FULL report link: say so, loudly.
    if (
      !signedAttachment &&
      sections &&
      !Array.isArray((await res.json().catch(() => null))?.sections)
    )
      throw new Error(SECTIONS_NOT_SUPPORTED)
  } finally {
    if (timer) clearTimeout(timer)
  }
}
